import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Handshake } from 'lucide-react';
import { apiService } from '../services/apiService';

const Marcas = () => {
  const [marcas, setMarcas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMarcas = async () => {
      try {
        const response = await apiService.getMarcas();
        if (response.estado === 1) {
          // Filtrar solo las activas y ordenar por el campo 'orden'
          const marcasActivas = response.data
            .filter(marca => marca.activo === 1)
            .sort((a, b) => a.orden - b.orden);
          setMarcas(marcasActivas);
        }
      } catch (error) {
        console.error('Error cargando marcas:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMarcas();
  }, []);

  if (loading) {
    return (
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4">
          <div className="flex justify-center">
            <motion.div
              className="w-12 h-12 border-4 border-yellow-600 border-t-yellow-500 rounded-full"
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            />
          </div>
        </div>
      </section>
    );
  }

  if (marcas.length === 0) {
    return null;
  }

  return (
    <section className="py-20 lg:py-28 bg-black relative overflow-hidden">
      {/* Fondo decorativo sutil */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-px bg-gradient-to-r from-transparent via-yellow-600/30 to-transparent"></div>
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full h-px bg-gradient-to-r from-transparent via-yellow-600/30 to-transparent"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-yellow-600/5 rounded-full filter blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        {/* Encabezado de la sección */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 bg-yellow-600/10 border border-yellow-600/30 rounded-full px-4 py-1.5 mb-4">
            <Handshake className="w-4 h-4 text-yellow-500" />
            <span className="text-yellow-500 text-sm font-semibold tracking-wider uppercase">
              Aliados Estratégicos
            </span>
          </div>
          
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            Marcas que <span className="text-yellow-500">Confían</span> en Nosotros
          </h2>
          
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Trabajamos con las mejores marcas de la industria para garantizar 
            calidad, rendimiento y excelencia en cada evento.
          </p>
        </motion.div>

        {/* Grid de Marcas */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 lg:gap-8">
          {marcas.map((marca, index) => (
            <motion.a
              key={marca.id}
              href={marca.url_sitio}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ y: -8 }}
              className="group relative bg-neutral-900/50 backdrop-blur-sm border border-yellow-600/20 rounded-2xl p-6 lg:p-8 flex flex-col items-center justify-center h-40 lg:h-48 transition-all duration-500 hover:border-yellow-500/60 hover:shadow-[0_0_30px_-5px_rgba(234,179,8,0.15)]"
              aria-label={`Visitar sitio web de ${marca.nombre}`}
            >
              {/* Efecto de brillo en las esquinas al hacer hover */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-yellow-500/0 to-yellow-500/0 group-hover:from-yellow-500/5 group-hover:to-yellow-600/5 transition-all duration-500"></div>
              
              {/* Logo de la marca */}
              <div className="relative z-10 w-full h-full flex items-center justify-center">
                <img
                  src={marca.logo_url_completa}
                  alt={`Logo de ${marca.nombre}`}
                  className="max-w-full max-h-16 lg:max-h-20 object-contain transition-all duration-500 grayscale group-hover:grayscale-0 group-hover:scale-110"
                  loading="lazy"
                />
              </div>

              {/* Nombre de la marca (visible solo en hover o siempre, según preferencia) */}
              <div className="absolute bottom-3 left-0 right-0 text-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <span className="text-xs text-yellow-500 font-medium uppercase tracking-wider flex items-center justify-center gap-1">
                  Visitar sitio <ExternalLink className="w-3 h-3" />
                </span>
              </div>

              {/* Borde animado inferior */}
              <motion.div
                className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-yellow-500 to-transparent"
                initial={{ scaleX: 0 }}
                whileHover={{ scaleX: 1 }}
                transition={{ duration: 0.4 }}
              />
            </motion.a>
          ))}
        </div>

        {/* Call to action opcional */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5, duration: 0.6 }}
          className="text-center mt-16"
        >
          <p className="text-gray-500 text-sm">
            ¿Representas a una marca y te gustaría ser parte de nuestros aliados?
          </p>
          <a 
            href="/contacto" 
            className="inline-block mt-3 text-yellow-500 hover:text-yellow-400 font-semibold text-sm border-b border-yellow-600/50 hover:border-yellow-500 transition-colors pb-0.5"
          >
            Contáctanos para colaborar
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Marcas;